import { NavLink, Outlet, Link } from "react-router-dom";
import { Home, CalendarCheck, MessageCircle, Sparkles, User, MapPin, ChevronDown } from "lucide-react";
import AuthSheet from "./AuthSheet";
import { useCustomer } from "../lib/auth";

const TABS = [
  { to: "/app", icon: Home, label: "Home", end: true },
  { to: "/app/bookings", icon: CalendarCheck, label: "Bookings" },
  { to: "/app/messages", icon: MessageCircle, label: "Messages" },
  { to: "/app/puromitra", icon: Sparkles, label: "PuroMitra" },
  { to: "/app/profile", icon: User, label: "Profile" },
];

const LocationPill = () => (
  <button className="flex items-center gap-2 rounded-full border border-[#EAD9C4] bg-white/80 px-3 py-1.5 backdrop-blur transition hover:border-[#8B1A2B]/40" data-testid="cust-location">
    <MapPin size={14} className="text-[#8B1A2B]" />
    <span className="text-left leading-tight">
      <span className="block text-[9px] font-semibold uppercase tracking-wide text-[#A0968A]">Service location</span>
      <span className="block text-xs font-semibold text-[#2A211C]">Rajajinagar</span>
    </span>
    <ChevronDown size={13} className="text-[#A0968A]" />
  </button>
);

export default function CustomerLayout() {
  const { user, setAuthOpen } = useCustomer();
  const initial = (user?.name || user?.email || "U")[0]?.toUpperCase();

  return (
    <div className="pc-grain min-h-screen bg-[#FBF6EF] font-body text-[#2A211C]">
      {/* ===== Desktop / tablet top navbar ===== */}
      <header className="sticky top-0 z-40 hidden border-b border-[#EFE6D8] bg-[#FBF6EF]/85 backdrop-blur-xl md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 py-3 lg:px-8">
          <Link to="/app" className="flex items-center gap-2.5" data-testid="cust-brand">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#8B1A2B] font-heading text-lg text-white">ॐ</span>
            <span className="font-heading text-xl tracking-tight text-[#8B1A2B]">PurohithConnect™</span>
          </Link>
          <nav className="flex items-center gap-1">
            {TABS.map((t) => (
              <NavLink key={t.to} to={t.to} end={t.end} data-testid={`cust-nav-${t.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition ${isActive ? "bg-[#8B1A2B] text-white shadow-[0_6px_18px_-8px_rgba(139,26,43,0.7)]" : "text-[#6C584C] hover:bg-[#F1E4D3]"}`}>
                <t.icon size={16} /> {t.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <LocationPill />
            {user ? (
              <NavLink to="/app/profile" data-testid="cust-avatar" className="grid h-9 w-9 place-items-center rounded-full bg-[#8B1A2B] font-heading text-sm text-white ring-2 ring-white transition hover:ring-[#E7C9CD]">{initial}</NavLink>
            ) : (
              <button onClick={() => setAuthOpen(true)} data-testid="cust-nav-signin" className="rounded-full bg-[#8B1A2B] px-4 py-2 text-sm font-semibold text-white transition active:scale-[0.98] hover:bg-[#6F1420]">Sign in</button>
            )}
          </div>
        </div>
      </header>

      {/* ===== Mobile top bar ===== */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-[#EFE6D8] bg-[#FBF6EF]/95 px-4 py-3 backdrop-blur md:hidden">
        <Link to="/app" className="flex items-center gap-2">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#8B1A2B] font-heading text-white">ॐ</span>
          <span className="font-heading text-base text-[#8B1A2B]">PurohithConnect™</span>
        </Link>
        <LocationPill />
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-0 pb-28 sm:px-4 md:pb-14 lg:px-8">
        <Outlet />
      </main>

      {/* ===== Mobile bottom tabs ===== */}
      <nav className="fixed bottom-0 left-0 z-40 w-full border-t border-[#EFE6D8] bg-white/95 backdrop-blur md:hidden">
        <div className="flex items-stretch justify-around px-2 py-1.5">
          {TABS.map((t) => (
            <NavLink key={t.to} to={t.to} end={t.end} data-testid={`cust-tab-${t.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-0.5 rounded-lg py-1.5 text-[10px] font-medium transition ${isActive ? "text-[#8B1A2B]" : "text-[#9A8F82]"}`}>
              <t.icon size={20} strokeWidth={2} />
              {t.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <AuthSheet />
    </div>
  );
}
